import React from 'react';
import { Box, Typography, Breadcrumbs, Link } from '@mui/material';
import { NavigateNext } from '@mui/icons-material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { colors, radii } from '../../styles/tokens';
import { StatusBadge, ToolStatus } from './StatusBadge';
import { DataSourceBadge, DataSourceType } from './DataSourceBadge';
import { toolNavItems } from '../Navigation/toolNavItems';

export interface BreadcrumbItem {
  label: string;
  to?: string;
}

export interface PageHeaderProps {
  title: string;
  description?: string;
  status?: ToolStatus;
  icon?: React.ReactNode;
  dataSource?: { label: string; type?: DataSourceType };
  // Explicit trail for non-tool pages (Resources, About, …). When omitted, tool
  // pages fall back to the auto-detected Home > Tools > [Tool] breadcrumb.
  breadcrumbs?: BreadcrumbItem[];
}

export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  description,
  status,
  icon,
  dataSource,
  breadcrumbs,
}) => {
  const location = useLocation();
  const tool = toolNavItems.find((t) => t.path === location.pathname);

  return (
    <Box
      component="header"
      sx={{
        mb: 3,
        pb: 2.5,
        borderBottom: `1px solid ${colors.border}`,
      }}
    >
      {breadcrumbs ? (
        <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: 2 }}>
          {breadcrumbs.map((crumb, i) => {
            const isLast = i === breadcrumbs.length - 1;
            if (crumb.to && !isLast) {
              return (
                <Link
                  key={crumb.label}
                  component={RouterLink}
                  to={crumb.to}
                  underline="hover"
                  color={colors.textSecondary}
                  fontSize="0.875rem"
                >
                  {crumb.label}
                </Link>
              );
            }
            return (
              <Typography
                key={crumb.label}
                variant="body2"
                color={isLast ? colors.textPrimary : colors.textSecondary}
                sx={isLast ? { fontWeight: 600 } : undefined}
              >
                {crumb.label}
              </Typography>
            );
          })}
        </Breadcrumbs>
      ) : (
        tool && (
          <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: 2 }}>
            <Link component={RouterLink} to="/" underline="hover" color={colors.textSecondary} fontSize="0.875rem">
              Home
            </Link>
            <Typography variant="body2" color={colors.textSecondary}>
              Tools
            </Typography>
            <Typography variant="body2" color={colors.textPrimary} sx={{ fontWeight: 600 }}>
              {tool.text}
            </Typography>
          </Breadcrumbs>
        )
      )}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        {icon && (
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: `${radii.button}px`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: `${colors.primary}14`,
              color: colors.primary,
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flexWrap: 'wrap',
              mb: description ? 1 : 0,
            }}
          >
            <Typography
              variant="h5"
              component="h1"
              sx={{
                fontWeight: 700,
                color: colors.textPrimary,
                letterSpacing: '-0.01em',
              }}
            >
              {title}
            </Typography>
            {status && <StatusBadge status={status} size="small" />}
            {dataSource && (
              <DataSourceBadge label={dataSource.label} type={dataSource.type} />
            )}
          </Box>
          {description && (
            <Typography
              variant="body2"
              sx={{
                color: colors.textSecondary,
                lineHeight: 1.6,
                maxWidth: 720,
              }}
            >
              {description}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};
